import {
  AnchorButton,
  Button,
  ButtonGroup,
  Checkbox,
  ControlGroup,
  FormGroup,
  Icon,
  IconName,
  InputGroup,
  Intent,
  NumericInput,
} from "@blueprintjs/core";
import _, { Dictionary } from "lodash";
import React from "react";
import { UserLanguage } from "../../lang/lang";
import { AppToaster } from "../../views/Template";
import { SuccessCard } from "../SuccessCard";
import { ArticleFieldListBuilder } from "./builder/ArticleFieldList";
import { DatePickerBuilder } from "./builder/DatePickerBuilder";
import { renderText } from "./builder/helper";
import { ItemSelectBuilder } from "./builder/ItemSelect";
import { OrderPositionsBuilder } from "./builder/OrderPositions";
import { QuantityScaleBuilder } from "./builder/QuantityScale";
import { StringListBuilder } from "./builder/StringList";
import { TextBuilder } from "./builder/TextBuilder";
import { ErrorCard } from "./Error";
import { InputFormState } from "./InputForm";
import { FormInputFields } from "./InputFormBuilder";

export enum ModalInputType {
  FixedItem = "FixedItem",
  Display = "Display",
  String = "String",
  URL = "URL",
  Text = "Text",
  ItemSelect = "ItemSelect",
  ArticleFieldList = "ArticleFieldList",
  CheckBox = "CheckBox",
  StringList = "StringList",
  Number = "Number",
  QuantityScale = "QuantityScale",
  OrderPositions = "OrderPositions",
  Date = "Date",
}

export interface BaseInputProperties<ObjectType, ObjectPropertType> {
  type: ModalInputType;
  placeholder?: string;
  intent?: (property: ObjectPropertType, root: ObjectType) => Intent;
  icon?:
    | IconName
    | ((property: ObjectPropertType, root: ObjectType) => IconName);
  label?: string;
  defaultValue?: any;
  readonly?: boolean;

  translate?: Dictionary<string>;
}

export interface ItemRenderInputProperties<ObjectType, ObjectPropertType>
  extends BaseInputProperties<ObjectType, ObjectPropertType> {
  itemRenderer?: (
    property: ObjectPropertType,
    root: ObjectType,
    submitting: boolean,
    onSelect: null | ((item: ObjectType) => void)
  ) => JSX.Element;
  itemKey?: keyof NonNullable<ObjectPropertType>;
}

export interface TextRenderInputProperties<ObjectType, ObjectPropertType>
  extends BaseInputProperties<ObjectType, ObjectPropertType> {
  textRenderer?: (property: ObjectPropertType, root: ObjectType) => string;
  itemKey?: keyof NonNullable<ObjectPropertType>;
}

export interface FixedItemInputProperties<ObjectType, ObjectPropertType>
  extends BaseInputProperties<ObjectType, ObjectPropertType> {
  type: ModalInputType.FixedItem;
}

export interface StringInputProperties<ObjectType, ObjectPropertType>
  extends BaseInputProperties<ObjectType, ObjectPropertType>,
    TextRenderInputProperties<ObjectType, ObjectPropertType> {
  type: ModalInputType.String;
  is_password?: boolean;
}

export interface CheckBoxInputProperties<ObjectType, ObjectPropertType>
  extends BaseInputProperties<ObjectType, ObjectPropertType>,
    TextRenderInputProperties<ObjectType, ObjectPropertType> {
  type: ModalInputType.CheckBox;
}

export interface ItemSelectInputProperties<ObjectType, ObjectPropertType>
  extends BaseInputProperties<ObjectType, ObjectPropertType>,
    ItemRenderInputProperties<ObjectType, ObjectPropertType>,
    TextRenderInputProperties<ObjectType, ObjectPropertType> {
  type: ModalInputType.ItemSelect;
  items?: Array<ObjectPropertType>;
}

export interface DisplayInputProperties<
  ObjectType = any,
  ObjectPropertType = any
> extends BaseInputProperties<ObjectType, ObjectPropertType>,
    TextRenderInputProperties<ObjectType, ObjectPropertType> {
  type: ModalInputType.Display;
}

export type InputProperties<ObjectType, ObjectPropertType> =
  | StringInputProperties<ObjectType, ObjectPropertType>
  | DisplayInputProperties<ObjectType, ObjectPropertType>
  | ItemRenderInputProperties<ObjectType, ObjectPropertType>
  | TextRenderInputProperties<ObjectType, ObjectPropertType>
  | ItemSelectInputProperties<ObjectType, ObjectPropertType>
  | CheckBoxInputProperties<ObjectType, ObjectPropertType>
  | FixedItemInputProperties<ObjectType, ObjectPropertType>;

type ModalInputTypeMap<T> = {
  [key in ModalInputType]: T;
};

export interface FormItemBuilderProperties<
  ObjectType = any,
  _InputProperties extends InputProperties<ObjectType, any> = any
> {
  field_properties: _InputProperties;
  field_index: string;
  update_value: (v: any) => void;
  reset_value: () => void;
  field_current_value: ObjectType[keyof ObjectType];
  field_root_value: ObjectType;
  form_state: InputFormState<ObjectType>;
  form_submitting: boolean;
  scratch_pad: any;
  field_changed: boolean;
}

export type FormItemBuilder<
  ObjectType = any,
  _I extends InputProperties<ObjectType, any> = any
> = (
  bundle: FormItemBuilderProperties<ObjectType, _I>
) => React.ReactNode | React.ReactNode[];

const FormBuilder: ModalInputTypeMap<FormItemBuilder> = {
  String: (
    bundle: FormItemBuilderProperties<any, StringInputProperties<any, any>>
  ) => {
    const items = [
      <InputGroup
        key={bundle.field_index}
        type={bundle.field_properties.is_password ? "password" : "text"}
        fill
        style={{ width: "100%" }}
        leftIcon={
          typeof bundle.field_properties.icon === "function"
            ? bundle.field_properties.icon(
                bundle.field_current_value,
                bundle.field_root_value
              )
            : bundle.field_properties.icon
        }
        intent={bundle.field_changed ? "primary" : "none"}
        disabled={
          bundle.form_state.submitting || bundle.field_properties.readonly
        }
        placeholder={bundle.field_properties.placeholder}
        value={bundle.form_state.values[bundle.field_index] || ""}
        onChange={({ currentTarget }: React.FormEvent<HTMLInputElement>) =>
          bundle.update_value(currentTarget.value)
        }
        rightElement={
          <ButtonGroup>
            <Button
              disabled={!bundle.field_changed}
              icon="reset"
              minimal
              onClick={() => bundle.reset_value()}
            />
            <Button
              disabled={
                bundle.field_current_value
                  ? bundle.field_current_value.length === 0
                  : true
              }
              onClick={() => {
                navigator.clipboard.writeText(bundle.field_current_value);
                AppToaster.show({
                  message: UserLanguage().actions.copy,
                  icon: "tick",
                  timeout: 1000,
                  intent: "success",
                });
              }}
              icon="duplicate"
              minimal
            />
          </ButtonGroup>
        }
      />,
    ];
    if (bundle.field_properties.translate) {
      let translated = _.get(
        bundle.field_properties.translate,
        bundle.field_current_value
      );
      if (translated)
        items.push(
          <InputGroup
            key={bundle.field_index + "_translate"}
            type="text"
            disabled
            value={translated}
          />
        );
    }
    return items;
  },
  URL: (
    bundle: FormItemBuilderProperties<any, StringInputProperties<any, any>>
  ) => {
    let link_button = undefined;
    if (bundle.field_current_value && bundle.field_current_value.length > 0) {
      link_button = (
        <AnchorButton href={bundle.field_current_value} icon="share" minimal />
      );
    }

    const items = [
      <InputGroup
        key={bundle.field_index}
        type={bundle.field_properties.is_password ? "password" : "text"}
        fill
        style={{ width: "100%" }}
        leftIcon={
          typeof bundle.field_properties.icon === "function"
            ? bundle.field_properties.icon(
                bundle.field_current_value,
                bundle.field_root_value
              )
            : bundle.field_properties.icon
        }
        intent={bundle.field_changed ? "primary" : "none"}
        disabled={
          bundle.form_state.submitting || bundle.field_properties.readonly
        }
        placeholder={bundle.field_properties.placeholder}
        value={bundle.form_state.values[bundle.field_index] || ""}
        onChange={({ currentTarget }: React.FormEvent<HTMLInputElement>) =>
          bundle.update_value(currentTarget.value)
        }
        rightElement={
          <ButtonGroup>
            <Button
              disabled={
                bundle.field_current_value
                  ? bundle.field_current_value.length === 0
                  : true
              }
              onClick={() => {
                navigator.clipboard.writeText(bundle.field_current_value);
                AppToaster.show({
                  message: UserLanguage().actions.copy,
                  icon: "tick",
                  timeout: 1000,
                  intent: "success",
                });
              }}
              icon="duplicate"
              minimal
            />
            {link_button}
          </ButtonGroup>
        }
      />,
    ];

    if (bundle.field_properties.translate) {
      let translated = _.get(
        bundle.field_properties.translate,
        bundle.field_current_value
      );
      if (translated)
        items.push(
          <InputGroup
            key={bundle.field_index + "_translate"}
            type="text"
            disabled
            value={translated}
          />
        );
    }
    return items;
  },
  Display: (bundle: FormItemBuilderProperties<any, DisplayInputProperties>) => (
    <InputGroup
      key={bundle.field_index}
      type="text"
      fill
      style={{ width: "100%" }}
      leftIcon={
        typeof bundle.field_properties.icon === "function"
          ? bundle.field_properties.icon(
              bundle.field_current_value,
              bundle.field_root_value
            )
          : bundle.field_properties.icon
      }
      disabled={true}
      value={renderText(bundle.field_current_value, bundle)}
    />
  ),
  FixedItem: (
    __: FormItemBuilderProperties<any, FixedItemInputProperties<any, any>>
  ) => <></>,
  CheckBox: (bundle: FormItemBuilderProperties<any>) => (
    <ControlGroup>
      <Checkbox
        key={bundle.field_index}
        checked={bundle.field_current_value || false}
        disabled={
          bundle.form_state.submitting || bundle.field_properties.readonly
        }
        onChange={(v) => bundle.update_value(v.currentTarget.checked)}
        label={bundle.field_properties.label}
        large
      >
        {bundle.field_properties.icon ? (
          <Icon
            icon={
              typeof bundle.field_properties.icon === "function"
                ? bundle.field_properties.icon(bundle.field_current_value)
                : bundle.field_properties.icon
            }
          />
        ) : (
          <></>
        )}
      </Checkbox>
      {bundle.form_submitting ? (
        <Icon icon="dot" intent="none" />
      ) : bundle.field_changed ? (
        <Icon icon="dot" intent="primary" />
      ) : (
        <></>
      )}
    </ControlGroup>
  ),
  Number: (bundle) => (
    <NumericInput
      key={bundle.field_index}
      value={bundle.field_current_value}
      min={bundle.field_properties.numeric_min}
      max={bundle.field_properties.numeric_max}
      onChange={(v) => bundle.update_value(v)}
      allowNumericCharactersOnly={
        bundle.field_properties.numeric_float ? true : false
      }
      onValueChange={(a, b, c) => bundle.update_value(a)}
      minorStepSize={bundle.field_properties.numeric_float ? 0.1 : 1}
      clampValueOnBlur
      fill
      asyncControl={false}
      intent={bundle.field_changed ? "primary" : "none"}
    />
  ),
  StringList: StringListBuilder,
  Text: TextBuilder,
  ItemSelect: ItemSelectBuilder,
  ArticleFieldList: ArticleFieldListBuilder,
  QuantityScale: QuantityScaleBuilder,
  OrderPositions: OrderPositionsBuilder,
  Date: DatePickerBuilder,
};

const no_form_group: Array<ModalInputType> = [
  ModalInputType.QuantityScale,
  ModalInputType.ArticleFieldList,
  ModalInputType.CheckBox,
];

export const buildFormElement = <ObjectType,>(
  root: ObjectType,
  index: string,
  set_state: (args: any) => void,
  state: InputFormState<ObjectType>,
  submitting: boolean,
  scratchPad: any,
  inputProperty: InputProperties<any, any>
) => {
  const bundle: FormItemBuilderProperties = {
    field_properties: inputProperty,
    field_index: index,
    update_value: (v) => {
      set_state({
        ...state,
        values: _.set(_.clone(state.values), index, v),
      });
    },
    reset_value: () => {
      let reset_val;
      if (_.get(state.original_input, index)) {
        reset_val = _.get(state.original_input, index);
      } else if (inputProperty.defaultValue) {
        reset_val = inputProperty.defaultValue;
      } else {
        reset_val = null;
      }

      set_state({
        ...state,
        values: _.set(_.clone(state.values), index, reset_val),
      });
    },
    field_current_value: _.get(state.values, index),
    form_state: state,
    form_submitting: submitting,
    scratch_pad: scratchPad,
    field_root_value: root,
    field_changed:
      _.get(state.values, index) !== _.get(state.original_input, index),
  };
  // console.log("building input", { state, bundle });
  return FormBuilder[inputProperty.type](bundle);
};

export interface InputBuilderProps<ObjectType> {
  state: InputFormState<ObjectType>;
  success_text: string;
  keys: FormInputFields<ObjectType>;
  root: ObjectType;
  set_state: (v: any) => void;
  changed: boolean;
}

export interface SingleBuilderProps<ObjectType> {
  state: InputFormState<ObjectType>;
  index: string;
  field?: InputProperties<ObjectType, any>;
  root: Partial<ObjectType>;
  set_state: (v: any) => void;
  changed: boolean;
}

export const InputBuilderSingle: <ObjectType>(
  props: SingleBuilderProps<ObjectType>
) => JSX.Element | null = (props) => {
  if (!props.field) return null;

  // console.log("build input single", props);

  let element = buildFormElement(
    props.root,
    props.index,
    (a: any) => props.set_state({ ...a, error: null }),
    props.state,
    props.state.submitting,
    props.state.scratch_pad,
    props.field
  );

  // console.log("got element", element);

  // let icon = (
  //   <Icon
  //     icon="dot"
  //     intent="primary"
  //     style={{ display: props.changed ? "inherit" : "none" }}
  //   />
  // );

  if (element)
    if (_.indexOf(no_form_group, props.field.type) !== -1)
      return <>{element}</>;
    else
      return (
        <FormGroup
          label={props.field.label}
          key={"fg_" + props.state.random_id + "_" + props.index}
          disabled={props.state.submitting}
        >
          <ControlGroup fill style={{ alignItems: "center" }}>
            {element}
            {/* {icon} */}
          </ControlGroup>
        </FormGroup>
      );
  else return null;
};

export const InputCardBuilder = (props: InputBuilderProps<any>) => {
  const { state, success_text: successText } = props;
  return (
    <div key="cards_builder">
      <ErrorCard error={state.error} />
      <SuccessCard
        text={successText}
        display={state.error === null && state.success}
      />
    </div>
  );
};

// convert internal state to raw
export function ConvertInputForSubmit<ObjectType, MutationVariables>(
  keys: FormInputFields<ObjectType>,
  values: Partial<ObjectType>
): Partial<MutationVariables> {
  let new_values: Partial<MutationVariables> = {};

  _.forEach(values, (value, key) => {
    const builder = _.get(keys, key) as InputProperties<any, any> | null;

    console.log("converting for submit 1", new_values, builder);

    if (builder === null) {
      return;
    }

    if (builder.type === ModalInputType.FixedItem)
      _.set(
        new_values,
        key,
        value !== undefined && value !== null ? value : builder.defaultValue
      );
    else if (value !== undefined) _.set(new_values, key, value);
    else if (builder.defaultValue)
      _.set(new_values, key, value || builder.defaultValue);

    console.log("converting for submit 2", new_values);
  });

  return new_values;
}

export const InputBuilder = (props: InputBuilderProps<any>) => (
  <div key={"input_builder_" + _.keys(props.keys).join("_")}>
    {InputCardBuilder(props)}
    {_.map(props.keys, (keyProp, index) => (
      <InputBuilderSingle
        set_state={props.set_state}
        field={keyProp}
        index={index}
        state={props.state}
        key={index}
        root={props.root}
        changed={props.changed}
      />
    ))}
  </div>
);
